/**
 * Resolve the email off a response/attendee row using one canonical precedence.
 *
 * Forms whose email field isn't literally named "Email" only expose the reliable
 * lowercase `email` column, so that wins; the legacy capital `Email` and the
 * nested `response_data.Email`/`response_data.email` are fallbacks for older or
 * unflattened rows. This is the single source of truth for "get the email off a
 * row" — every call site (client-side diffing and server-side lookups alike)
 * should use it instead of re-inlining the fallback chain, which previously
 * drifted into conflicting orderings across the feature.
 *
 * @param row - A flattened response/attendee row, or a raw `form_responses` row.
 * @returns The first defined email value, or `null` when the row has none.
 */
export const getRowEmail = (row: Record<string, any> | null | undefined): string | null => {
  if (!row) return null;
  return row.email || row.Email || row.response_data?.Email || row.response_data?.email || null;
};

/**
 * Build a `response_id -> row` lookup for status-update resolution.
 *
 * `response_id` is the table's primary key, so there's no first-wins collision
 * to worry about — this exists so status updates resolve the exact row a staff
 * action targeted instead of re-deriving it from email, which silently
 * misroutes when two responses share an email address.
 *
 * @param attendees - Flattened response/attendee rows.
 * @returns Map keyed by `response_id`.
 */
export const buildAttendeeResponseIdLookup = (attendees: any[]): Map<number, any> => {
  const byResponseId = new Map<number, any>();
  for (const row of attendees) {
    if (typeof row.response_id === "number") byResponseId.set(row.response_id, row);
  }
  return byResponseId;
};

export const isValidEmail = (email: string): boolean => {
  const trimmed = email.trim();
  if (!trimmed) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
};

export const parseEmails = (input: string): string[] => {
  return input
    .split(/[\n,;]+/)
    .map((email) => email.trim())
    .filter(isValidEmail);
};

export const normalizeEmail = (email: string | null | undefined): string | null => {
  if (!email) return null;
  return email.toLowerCase().trim();
};
